import { useEffect, useRef } from 'react';

import { getCookieValue, setRawCookie } from 'Utilities';

import {
  CONSENT_CHANGED_EVENT,
  CONSENT_COOKIE_EXPIRY_DAYS,
  CONSENT_COOKIE_NAME,
  CONSENT_UPDATED_DATALAYER_EVENT,
  ESSENTIALS_ONLY_CONSENT,
} from './cookieConsent.constants';
import type { ConsentObject, ConsentValue } from './cookieConsent.types';

export const hasStoredConsent = (): boolean => Boolean(getCookieValue(CONSENT_COOKIE_NAME));

const isConsentValue = (value: unknown): value is ConsentValue => value === 'granted' || value === 'denied';

export const validateConsent = (parsed: unknown): ConsentObject => {
  if (!parsed || typeof parsed !== 'object') {
    return ESSENTIALS_ONLY_CONSENT;
  }

  const source = parsed as Record<string, unknown>;

  return (Object.keys(ESSENTIALS_ONLY_CONSENT) as (keyof ConsentObject)[]).reduce(
    (acc, key) => {
      const value = source[key];
      acc[key] = isConsentValue(value) ? value : ESSENTIALS_ONLY_CONSENT[key];
      return acc;
    },
    {} as ConsentObject,
  );
};

export const getStoredConsent = (): ConsentObject => {
  const raw = getCookieValue(CONSENT_COOKIE_NAME);

  if (!raw) {
    return ESSENTIALS_ONLY_CONSENT;
  }

  try {
    return validateConsent(JSON.parse(raw));
  } catch {
    return ESSENTIALS_ONLY_CONSENT;
  }
};

export const storeConsent = (consent: ConsentObject): boolean => {
  const serialised = JSON.stringify(consent);
  setRawCookie(CONSENT_COOKIE_NAME, serialised, CONSENT_COOKIE_EXPIRY_DAYS);
  return getCookieValue(CONSENT_COOKIE_NAME) === serialised;
};

export const pushConsentToGTM = (consent: ConsentObject): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  // eslint-disable-next-line func-names, @typescript-eslint/no-unused-vars
  function gtag(_command: 'consent', _action: 'update', _consent: ConsentObject) {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments as unknown as Record<string, unknown>);
  }

  gtag('consent', 'update', consent);

  window.dataLayer.push({ consent, event: CONSENT_UPDATED_DATALAYER_EVENT });
};

export const applyConsent = (consent: ConsentObject): boolean => {
  const persisted = storeConsent(consent);
  pushConsentToGTM(consent);

  if (persisted && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
  }

  return persisted;
};

export const useConsentChangeListener = (onChange: () => void, enabled: boolean = true): void => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleConsentChanged = () => {
      onChangeRef.current();
    };

    handleConsentChanged();

    window.addEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);

    return () => {
      window.removeEventListener(CONSENT_CHANGED_EVENT, handleConsentChanged);
    };
  }, [enabled]);
};
